import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Mail, User, Users, Globe } from "lucide-react";
import { PasswordInput } from "./PasswordInput";
import { SUPPORTED_LANGUAGES, getSectionTranslations } from "@/i18n";

const SUBJECTS = [
  { id: "math", name: "Mathematics" },
  { id: "science", name: "Science" },
  { id: "english", name: "English Grammar" },
  { id: "hindi", name: "Hindi Vyakaran" }
];
// Physics/Chemistry/Biology offered as separate subjects for Grade 7-8 only.
const EXTENDED_SUBJECTS_78 = [
  { id: "physics", name: "Physics" },
  { id: "chemistry", name: "Chemistry" },
  { id: "biology", name: "Biology" }
];
const subjectsForGrade = (grade) => {
  const g = parseInt(grade, 10);
  return (g >= 7) ? [...SUBJECTS, ...EXTENDED_SUBJECTS_78] : SUBJECTS;
};

export function RegistrationSuccess({ data, onLogin }) {
  return (
    <div className="space-y-6 text-center">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
        <User className="h-10 w-10 text-green-600" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900">Registration Successful!</h2>
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-left">
        <p className="text-sm text-green-800"><strong>Student ID:</strong> {data.student_id}</p>
        <p className="text-sm text-green-800"><strong>Child:</strong> {data.child_name}</p>
        <p className="text-sm text-green-800"><strong>Free Trial:</strong> 90 days with all Pro features</p>
      </div>
      <p className="text-gray-600 text-sm">A welcome email has been sent with all the details.</p>
      <Button 
        onClick={onLogin}
        className="w-full bg-blue-600 hover:bg-blue-700"
      >
        Login Now
      </Button>
    </div>
  );
}

export function RegistrationStep1({ parentName, setParentName, email, setEmail, loading, onContinue }) {
  return (
    <div className="space-y-6">
      <a 
        href="/"
        className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
      >
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Home
      </a>

      <div className="text-center">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Users className="h-8 w-8 text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Parent Registration</h2>
        <p className="text-gray-600 mt-2">Register your child for Eduzy learning</p>
      </div>

      <div className="space-y-4">
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            value={parentName}
            onChange={(e) => setParentName(e.target.value)}
            placeholder="Your Full Name"
            className="pl-10"
          />
        </div>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your Email Address"
            className="pl-10"
          />
        </div>
      </div>

      <Button 
        onClick={onContinue}
        disabled={loading}
        className="w-full bg-blue-600 hover:bg-blue-700"
      >
        {loading ? "Sending..." : "Continue"}
      </Button>

      <p className="text-center text-xs text-gray-500">
        We&apos;ll send a verification code to your email
      </p>
    </div>
  );
}

export function RegistrationStep2({ email, regOtp, setRegOtp, loading, onBack, onVerify, onChangeEmail }) {
  const [showChangeEmail, setShowChangeEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [changingEmail, setChangingEmail] = useState(false);

  const handleChangeEmail = async () => {
    if (!newEmail || !newEmail.includes('@')) {
      return;
    }
    setChangingEmail(true);
    try {
      await onChangeEmail(newEmail);
      setShowChangeEmail(false);
      setNewEmail('');
    } finally {
      setChangingEmail(false);
    }
  };

  return (
    <div className="space-y-6">
      <button 
        onClick={onBack}
        className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
      >
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </button>

      <div className="text-center">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Mail className="h-8 w-8 text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Verify Email</h2>
        <p className="text-gray-600 mt-2">
          Enter the code sent to <span className="font-medium">{email}</span>
        </p>
        {!showChangeEmail && (
          <button
            onClick={() => setShowChangeEmail(true)}
            className="text-blue-600 hover:text-blue-800 text-sm mt-1 underline"
          >
            Wrong email? Change it
          </button>
        )}
      </div>

      {showChangeEmail ? (
        <div className="space-y-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-800 font-medium">Enter your correct email address:</p>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Enter correct email"
              className="pl-10"
              autoFocus
            />
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => { setShowChangeEmail(false); setNewEmail(''); }}
              variant="outline"
              className="flex-1"
              disabled={changingEmail}
            >
              Cancel
            </Button>
            <Button
              onClick={handleChangeEmail}
              disabled={changingEmail || !newEmail || !newEmail.includes('@')}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              {changingEmail ? "Updating..." : "Update & Send Code"}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <Input
            value={regOtp}
            onChange={(e) => setRegOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            className="text-center text-3xl tracking-[0.5em] font-mono"
            maxLength={6}
          />

          <Button 
            onClick={onVerify}
            disabled={loading || regOtp.length !== 6}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            {loading ? "Verifying..." : "Verify Email"}
          </Button>
        </>
      )}
    </div>
  );
}

export function RegistrationStep3({
  childName, setChildName,
  childAge, setChildAge,
  childGrade, setChildGrade,
  board, setBoard,
  schoolName, setSchoolName,
  selectedSubjects, toggleSubject,
  password, setPassword,
  acceptTerms, setAcceptTerms,
  parentalConsent, setParentalConsent,
  loading,
  onBack,
  onComplete
}) {
  const [language, setLanguage] = useState('en');
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  
  // Get translations for current language
  const t = getSectionTranslations(language, 'consent');
  const commonT = getSectionTranslations(language, 'common');
  const currentLang = SUPPORTED_LANGUAGES.find(l => l.code === language);

  return (
    <div className="space-y-5">
      <button 
        onClick={onBack}
        className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
      >
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </button>

      <div className="text-center">
        <h2 className="text-xl font-bold text-gray-900">Child Details</h2>
        <p className="text-gray-600 text-sm mt-1">Tell us about your child</p>
      </div>

      <div className="space-y-3">
        <Input
          value={childName}
          onChange={(e) => setChildName(e.target.value)}
          placeholder="Child&apos;s Full Name"
        />
        
        <div className="grid grid-cols-2 gap-3">
          <Input
            type="number"
            value={childAge}
            onChange={(e) => setChildAge(e.target.value)}
            placeholder="Age"
            min={5}
            max={18}
          />
          <Select value={childGrade} onValueChange={setChildGrade}>
            <SelectTrigger>
              <SelectValue placeholder="Grade/Class" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="5">Grade 5</SelectItem>
              <SelectItem value="6">Grade 6</SelectItem>
              <SelectItem value="7">Grade 7</SelectItem>
              <SelectItem value="8">Grade 8</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <Input
          value={schoolName}
          onChange={(e) => setSchoolName(e.target.value)}
          placeholder="School Name"
        />

        <div>
          <p className="text-sm font-medium text-gray-700 mb-1">Curriculum Board</p>
          <Select value={board} onValueChange={setBoard}>
            <SelectTrigger data-testid="register-board-select">
              <SelectValue placeholder="Select Board" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cbse" data-testid="register-board-cbse">CBSE</SelectItem>
              <SelectItem value="ib" data-testid="register-board-ib">IB (International Baccalaureate)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[11px] text-gray-400 mt-1">
            We align content to your selected board to the best of our ability; occasional variations from the official syllabus may occur.
          </p>
        </div>

        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">Select Subjects</p>
          <div className="flex flex-wrap gap-2">
            {subjectsForGrade(childGrade).map(subject => (
              <button
                key={subject.id}
                type="button"
                data-testid={`register-subject-${subject.id}`}
                onClick={() => toggleSubject(subject.id)}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                  selectedSubjects.includes(subject.id)
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-400'
                }`}
              >
                {subject.name}
              </button>
            ))}
          </div>
        </div>

        <PasswordInput
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Create Password (min 8 chars)"
        />

        {/* DPDP Compliant Parental Consent Section with Multilingual Support */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
          {/* Language Selector for Consent */}
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-blue-900 text-sm flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              {t.title}
            </h4>
            
            {/* Language dropdown */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-blue-600">{commonT.chooseLanguage || "Choose your language"}</span>
              <div className="relative">
                <button
                  onClick={() => setShowLanguageSelector(!showLanguageSelector)}
                  className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-blue-700 hover:text-blue-900 bg-blue-100 hover:bg-blue-200 rounded-md transition-colors"
                  data-testid="consent-language-selector"
                >
                  <Globe className="w-3.5 h-3.5" />
                  <span>{currentLang?.nativeName}</span>
                </button>
              
              {showLanguageSelector && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[120px] z-10">
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        setLanguage(lang.code);
                        setShowLanguageSelector(false);
                      }}
                      className={`w-full px-3 py-1.5 text-left text-xs hover:bg-gray-100 flex items-center justify-between ${
                        language === lang.code ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                      data-testid={`consent-lang-${lang.code}`}
                    >
                      <span>{lang.nativeName}</span>
                      {language === lang.code && (
                        <span className="text-blue-600">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
              </div>
            </div>
          </div>
          
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={parentalConsent}
              onChange={(e) => setParentalConsent(e.target.checked)}
              className="mt-1 accent-blue-600"
              data-testid="parental-consent-checkbox"
            />
            <span className="text-gray-700">
              {t.intro}
              <ul className="list-disc ml-4 mt-1 text-gray-600 space-y-1">
                {t.points && t.points.map((point, idx) => (
                  <li key={idx}>{point}</li>
                ))}
              </ul>
              <span className="block mt-2 text-xs text-gray-500">
                {t.withdrawNote}
              </span>
            </span>
          </label>
        </div>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={acceptTerms}
            onChange={(e) => setAcceptTerms(e.target.checked)}
            className="mt-1"
          />
          <span className="text-gray-600">
            I agree to the <a href="/terms" className="text-blue-600 hover:underline">Terms of Service</a> and{" "}
            <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>
          </span>
        </label>
      </div>

      <Button 
        onClick={onComplete}
        disabled={loading || !acceptTerms || !parentalConsent}
        className="w-full bg-blue-600 hover:bg-blue-700"
      >
        {loading ? "Creating Account..." : "Complete Registration"}
      </Button>
    </div>
  );
}
